import { Imports } from '@gleamer/types';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  IMPORTS_REFRESH_MS,
  useImportBatches,
} from '../../../../hooks/useImports';
import { PageButton } from '../../../atoms/PageButton';
import { Card, Field, Pager, selectClassName } from '../../../atoms/Surface';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { fileBreakdown, formatDateTime } from './format';
import { importStatusLabel, ImportStatusBadge } from './ImportStatusBadge';
import { DATE_LOCALE } from '../../../../utils/locale';

type ImportBatch = Imports.ImportBatch;
type ImportBatchStatus = Imports.ImportBatchStatus;

const { IMPORT_BATCH_STATUSES, isFinalStatus } = Imports;

export const IMPORT_JOBS_PAGE_SIZE = 20;

/** The filter and page live in the URL, so a batch's page can link back to the list as it was. */
function useListParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get('status') || undefined) as
    | ImportBatchStatus
    | undefined;
  const page = Math.max(0, parseInt(searchParams.get('page') ?? '0', 10) || 0);

  const set = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key)
    );
    setSearchParams(next);
  };

  return { status, page, set, searchParams };
}

/** Files done (exported or already on the platform) out of all, and the errors. */
function Progress({ batch }: { batch: ImportBatch }) {
  const b = fileBreakdown(batch.counters);
  const problems = [
    b.importErrors > 0 && `${b.importErrors} import errors`,
    b.exportErrors > 0 && `${b.exportErrors} export errors`,
    batch.lines.notFound > 0 && `${batch.lines.notFound} lines not found`,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <span className="whitespace-nowrap tabular-nums">
      {b.done.toLocaleString()} / {batch.counters.files.toLocaleString()}
      {problems && (
        <span className="block whitespace-normal text-sm text-dl-danger">{problems}</span>
      )}
    </span>
  );
}

export function ImportJobsList() {
  const { status: statusFilter, page, set, searchParams } = useListParams();
  const { data, status, error, refetch, isFetching, dataUpdatedAt } =
    useImportBatches({
      page,
      size: IMPORT_JOBS_PAGE_SIZE,
      status: statusFilter,
    });
  const anyUnfinished =
    data?.content?.some(batch => !isFinalStatus(batch.status)) ?? false;

  return (
    <Card
      id="import-batches-title"
      title="Batches"
      description={
        <>
          {dataUpdatedAt > 0 && `Updated at ${new Date(dataUpdatedAt).toLocaleTimeString(DATE_LOCALE)}. `}
          {anyUnfinished &&
            `Refreshed every ${IMPORTS_REFRESH_MS / 1000} s while a batch is unfinished.`}
        </>
      }
      actions={
        <>
          <Field label="Status">
            <select
              className={selectClassName}
              value={statusFilter ?? ''}
              onChange={e => set({ status: e.target.value || undefined, page: undefined })}
            >
              <option value="">All</option>
              {IMPORT_BATCH_STATUSES.map(s => (
                <option key={s} value={s}>
                  {importStatusLabel(s)}
                </option>
              ))}
            </select>
          </Field>
          <PageButton
            onClick={() => refetch()}
            disabled={isFetching}
            className="mx-0 h-10 px-5"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </PageButton>
        </>
      }
    >
      {status === 'loading' && <div className="text-dl-muted">Loading…</div>}
      {status === 'error' && (
        <div role="alert" className="text-dl-danger">
          Could not load the import batches: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && data.content.length === 0 && (
        <div className="py-8 text-center text-lg text-dl-muted">
          No import batch{statusFilter ? ' with this status' : ' yet'}.
        </div>
      )}
      {status === 'success' && data.content.length > 0 && (
        <>
          <div className="-mx-6 overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <TableHeadTh className="pl-6">Batch</TableHeadTh>
                  <TableHeadTh>Status</TableHeadTh>
                  <TableHeadTh>Name</TableHeadTh>
                  <TableHeadTh>Source</TableHeadTh>
                  <TableHeadTh className="text-right">Lines</TableHeadTh>
                  <TableHeadTh>Requested</TableHeadTh>
                  <TableHeadTh className="pr-6 text-right">Files done</TableHeadTh>
                </tr>
              </thead>
              <tbody>
                {data.content.map(batch => (
                  <tr key={batch.id} className="group">
                    <TableBodyTd className="pl-6">
                      <Link
                        className="font-medium text-dl-accent hover:underline"
                        to={`/backoffice/imports/${batch.id}`}
                        state={{ listSearch: searchParams.toString() }}
                      >
                        #{batch.id}
                      </Link>
                    </TableBodyTd>
                    <TableBodyTd>
                      <ImportStatusBadge status={batch.status} progressAt={batch.progressAt} />
                    </TableBodyTd>
                    <TableBodyTd className="max-w-xs break-words">{batch.name}</TableBodyTd>
                    <TableBodyTd
                      className="max-w-[14rem] truncate font-mono text-sm text-dl-muted"
                      title={`gs://${batch.bucket}`}
                    >
                      gs://{batch.bucket}
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap text-right tabular-nums">
                      {batch.lines.done.toLocaleString()} / {batch.lines.total.toLocaleString()}
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap" title={batch.requestedBy}>
                      {formatDateTime(batch.requestedAt)}
                    </TableBodyTd>
                    <TableBodyTd className="pr-6 text-right">
                      <Progress batch={batch} />
                    </TableBodyTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={data.number}
            pages={data.totalPages}
            total={data.totalElements}
            noun="batches"
            onPage={next => set({ page: next > 0 ? String(next) : undefined })}
          />
        </>
      )}
    </Card>
  );
}
